import { Component, OnInit, AfterViewInit, ViewChildren, ElementRef, QueryList, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';

@Component({
  selector: 'app-signup-code',
  templateUrl: './signup-code.component.html',
  styleUrls: ['./signup-code.component.scss']
})
export class SignupCodeComponent implements OnInit, AfterViewInit {
  loading: boolean = false;
  email: string = '';
  code: string = '';
  resendingCode: boolean = false;
  codeDigits: string[] = Array(6).fill('');
  private advancingProgrammatically = false;

  @ViewChildren('digitInput') digitInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('verifyBtn', { read: ElementRef }) verifyBtn!: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private authService: AuthService,
    private toaster: ToasterService
  ) { }

  ngOnInit(): void {
    // Retrieve email from session storage
    const storedEmail = sessionStorage.getItem('signupEmail');
    if (!storedEmail) {
      // If no email is found, redirect back to the first step
      this.toaster.warning('Please start the signup process from the beginning', 4000);
      this.router.navigate(['/auth/signup/email']);
      return;
    }

    this.email = storedEmail;
  }

  ngAfterViewInit(): void {
    // Auto-focus the first input
    setTimeout(() => this.focusInput(0), 0);
  }

  public codeDigitsIsEmpty(): boolean {
    return this.codeDigits.every(d => d === '');
  }

  private focusInput(index: number): void {
    const el = this.digitInputs?.get(index)?.nativeElement;
    if (el) {
      setTimeout(() => {
        el.focus();
        el.select?.();
      }, 0);
    }
  }

  private focusSubmit(): void {
    const host = this.verifyBtn?.nativeElement as HTMLElement | undefined;
    const innerBtn = host?.querySelector('button') as HTMLButtonElement | null;
    (innerBtn ?? host)?.focus?.();
  }

  public isCodeComplete(): boolean {
    return this.codeDigits.join('').length === 6;
  }

  /**
   * Verifies the 6-digit code and proceeds to password creation
   */
  public async verifyCode(): Promise<void> {
    try {
      this.loading = true;

      // Guard against missing code
      this.code = this.codeDigits.join('');
      if (!this.code || this.code.length !== 6) {
        this.toaster.warning('Please enter the 6-digit verification code', 4000);
        this.loading = false;
        return;
      }

      // Verify the code
      const isValid = await this.authService.verifySignupCode(this.email, this.code);

      if (isValid) {
        // Store code in session storage for the final step
        sessionStorage.setItem('signupCode', this.code);
        this.router.navigate(['/auth/signup/password']);
      } else {
        this.toaster.warning('Invalid verification code. Please try again.', 4000);
      }
    } catch (error: any) {
      console.error('Code verification error:', error.message || error);
      this.toaster.warning(`Error: ${error.message || 'Failed to verify code'}`, 4000);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Go back to the email step
   */
  public goBack(): void {
    this.router.navigate(['/auth/signup/email']);
  }

  /**
   * Resend the verification code to the email on file
   */
  public async resendCode(): Promise<void> {
    try {
      if (!this.email) {
        const storedEmail = sessionStorage.getItem('signupEmail');

        if (storedEmail) {
          this.email = storedEmail;
        }
      }

      if (!this.email) {
        this.toaster.warning('No email found. Please start from the email step.', 4000);
        return;
      }

      this.resendingCode = true;
      const ok = await this.authService.resendSignupCode(this.email);
      if (ok) {
        this.toaster.success(`A new verification code has been sent to ${this.email}.`, 4000);
      } else {
        this.toaster.warning('Failed to resend verification code.', 4000);
      }
    } catch (error: any) {
      this.toaster.warning(`Error: ${error?.message || 'Failed to resend code'}`, 4000);
    } finally {
      this.resendingCode = false;
    }
  }

  /**
   * Clear all code inputs and focus the first cell
   */
  public clearCode(): void {
    this.codeDigits = Array(6).fill('');
    this.code = '';
    this.advancingProgrammatically = false;
    // Clear the native input values to be safe
    this.digitInputs?.forEach(ref => (ref.nativeElement.value = ''));
    // Focus first input on next tick
    setTimeout(() => this.focusInput(0), 0);
  }

  // Handle typing a digit and auto-advance
  public onDigitInput(event: Event, index: number): void {
    if (this.advancingProgrammatically) {
      // Skip handling because keydown flow already updated state and will advance focus
      this.advancingProgrammatically = false;
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, ''); // keep digits only

    if (value.length === 0) {
      this.codeDigits[index] = '';
      return;
    }

    // If user pasted multiple digits into a single box, distribute them
    const digits = value.split('').slice(0, 6 - index);
    digits.forEach((d, i) => {
      const pos = index + i;
      if (pos < 6) this.codeDigits[pos] = d;
    });

    if (this.isCodeComplete()) {
      this.focusSubmit();
    } else {
      const nextIndex = Math.min(index + digits.length, 5);
      this.focusInput(nextIndex);
    }
  }

  // Handle backspace to move to previous
  public onDigitKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    // If a numeric key is pressed, replace current value and advance
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      // Mark that we're advancing programmatically to ignore the following input event
      this.advancingProgrammatically = true;
      // Overwrite the current box immediately for visual feedback
      input.value = event.key;
      this.codeDigits[index] = event.key;
      // Defer focus move to avoid losing focus due to key event processing
      const advance = () => {
        if (this.isCodeComplete() || index === 5) {
          this.focusSubmit();
        } else {
          this.focusInput(index + 1);
        }
      };
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => setTimeout(advance, 0));
      } else {
        setTimeout(advance, 10);
      }
      return;
    }

    if (event.key === 'Backspace') {
      if (input.value) {
        // let normal backspace clear the current value
        return;
      }
      // Move focus to previous input
      const prev = Math.max(index - 1, 0);
      this.advancingProgrammatically = true;
      setTimeout(() => this.focusInput(prev), 0);
      return;
    } else if (event.key === 'ArrowLeft') {
      this.focusInput(Math.max(index - 1, 0));
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      this.focusInput(Math.min(index + 1, 5));
      event.preventDefault();
    }
  }

  // Handle paste of up to 6 digits
  public onDigitPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') || '';
    const digits = text.replace(/\D/g, '').split('');
    if (digits.length === 0) return;

    for (let i = 0; i < digits.length && index + i < 6; i++) {
      this.codeDigits[index + i] = digits[i];
    }
    const nextIndex = Math.min(index + digits.length, 5);
    if (this.isCodeComplete()) {
      this.focusSubmit();
    } else {
      const nextIndex = Math.min(index + digits.length, 5);
      this.focusInput(nextIndex);
    }
  }

  // Select existing value on focus so typing overwrites it
  public onDigitFocus(event: FocusEvent, _index: number): void {
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.select && input.select());
  }
}
